import React, { useState, useMemo } from "react";
import { useBotStore } from "../../stores/botStore";
import { Dropdown, AddModal, useErrorStore } from "components";

// Як бот читає список категорій: дозволені або заборонені
const CATEGORY_LISTS = [
  { value: "allow", label: "Дозволені" },
  { value: "deny", label: "Заборонені" },
];

// Пояснення для менеджера, що саме зробить бот при поточній комбінації
const categoryRuleHint = (list, strict) => {
  if (list === "deny") {
    return strict
      ? "Бере будь-яке замовлення, крім цих категорій."
      : "Уникає цих категорій, але бере їх, якщо інших замовлень немає.";
  }
  return strict
    ? "Бере лише замовлення з цих категорій."
    : "Спершу ці категорії, а якщо таких замовлень немає — будь-яке.";
};

// Компонент модалки для редагування налаштувань менеджера
const ManagerSettingsModal = ({ manager, onClose }) => {
  // Отримуємо дані та дії зі стору
  const updateSettings = useBotStore((state) => state.updateManagerSettings);
  const sources = useBotStore((state) => state.sources);
  const categoryOptions = useBotStore((state) => state.categoryOptions);
  const categoriesTree = useBotStore((state) => state.categoriesTree);
  const [activeTab, setActiveTab] = useState("settings");
  const [includeSubcategories, setIncludeSubcategories] = useState(true);
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  // Визначаємо початковий стан для порівняння
  const initialFormState = useMemo(() => {
    const {
      price_range: p = {},
      allowed_source_ids: sources = [],
      category: initialCategoryObj = { setting: true, category_ids: [] },
    } = manager.personal_settings || {};

    // Без поля strict суворість береться зі старого setting (true = гнучкий)
    let categoryList = "allow";
    let categoryStrict = false;
    if (!Array.isArray(initialCategoryObj)) {
      const { list, strict, setting = true } = initialCategoryObj || {};
      categoryList = list === "deny" ? "deny" : "allow";
      categoryStrict = typeof strict === "boolean" ? strict : !setting;
    }
    const categoryIds = Array.isArray(initialCategoryObj)
      ? initialCategoryObj
      : initialCategoryObj?.category_ids || [];

    return {
      min: p.min ?? "",
      max: p.max ?? "",
      sources,
      categoryIds,
      categoryList,
      categoryStrict,
    };
  }, [manager.personal_settings]);

  // Ініціалізуємо форму початковим станом
  const [form, setForm] = useState(initialFormState);

  // Обчислюємо, чи були зміни (isDirty)
  const isDirty = useMemo(() => {
    const isMinChanged = form.min !== initialFormState.min;
    const isMaxChanged = form.max !== initialFormState.max;
    const isSettingChanged =
      form.categoryList !== initialFormState.categoryList ||
      form.categoryStrict !== initialFormState.categoryStrict;

    // Перевірка масивів (навіть якщо змінився порядок елементів, хоча в дропдаунах це рідкість)
    const isSourcesChanged =
      form.sources.length !== initialFormState.sources.length ||
      !form.sources.every((id) => initialFormState.sources.includes(id));

    const isCategoriesChanged =
      form.categoryIds.length !== initialFormState.categoryIds.length ||
      !form.categoryIds.every((id) => initialFormState.categoryIds.includes(id));

    return (
      isMinChanged ||
      isMaxChanged ||
      isSettingChanged ||
      isSourcesChanged ||
      isCategoriesChanged
    );
  }, [form, initialFormState]);

  const sourceOptions = useMemo(
    () => sources.map(({ id, name }) => ({ value: id, label: name || `Source ${id}` })),
    [sources],
  );

  // Функція для збереження налаштувань
  const handleSave = async () => {
    const min = form.min !== "" ? Number(form.min) : null;
    const max = form.max !== "" ? Number(form.max) : null;

    if (min !== null && max !== null && min > max) {
      return useErrorStore
        .getState()
        .showError("Мін. ціна не може бути більшою за макс.");
    }

    let finalCategories = [...form.categoryIds];

    if (includeSubcategories && finalCategories.length > 0) {
      const result = new Set(finalCategories);

      const getDescendants = (node) => {
        if (!node.children) return;
        node.children.forEach((child) => {
          result.add(child.id);
          getDescendants(child);
        });
      };

      const findNode = (nodes, id) => {
        for (const node of nodes) {
          if (node.id === id) return node;
          if (node.children) {
            const found = findNode(node.children, id);
            if (found) return found;
          }
        }
        return null;
      };

      finalCategories.forEach((id) => {
        const node = findNode(categoriesTree, id);
        if (node) {
          getDescendants(node);
        }
      });

      finalCategories = Array.from(result);
    }

    // Формуємо payload, видаляючи null значення для чистоти API
    const payload = {
      price_range: {
        ...(min !== null && { min }),
        ...(max !== null && { max }),
      },
      allowed_source_ids: form.sources,
      category: {
        list: form.categoryList,
        strict: form.categoryStrict,
        setting: !form.categoryStrict,
        category_ids: finalCategories,
      },
    };

    // Закриваємо модалку при успішному збереженні
    if (await updateSettings(manager.manager_id, payload)) {
      onClose();
    }
  };

  // Функція для оновлення значень форми
  const updateForm = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <AddModal
      isOpen={true}
      onClose={onClose}
      title={`Персональні налаштування: ${manager.manager_name}`}
    >
      <div className="add-modal-tabs">
        <button
          className={`tab-btn ${activeTab === "settings" ? "active" : ""}`}
          onClick={() => setActiveTab("settings")}
        >
          Налаштування
        </button>
        <button className="tab-btn" disabled title="В розробці">
          Історія
        </button>
      </div>

      <div className="add-modal-body">
        {activeTab === "settings" && (
          <>
            <div className="form-group">
              <label>Ціновий діапазон</label>
              <div className="range-inputs">
                {["min", "max"].map((key) => (
                  <div key={key} className="input-wrapper">
                    <span className="input-label">{key === "min" ? "Min" : "Max"}</span>
                    <input
                      type="number"
                      value={form[key]}
                      onChange={(e) => updateForm(key, e.target.value)}
                      placeholder={key === "min" ? "0" : "50000"}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="label-wrapper">
                <span>Дозволені джерела</span>
                {form.sources.length > 0 && (
                  <button
                    type="button"
                    className="label-clear-btn"
                    onClick={() => updateForm("sources", [])}
                  >
                    Очистити все
                  </button>
                )}
              </label>
              <Dropdown
                multiple
                options={sourceOptions}
                selected={form.sources}
                onChange={(val) => updateForm("sources", val)}
                placeholder="Виберіть джерела"
              />
            </div>

            <div className="form-group">
              <label className="label-wrapper">
                <span>Категорії</span>
                <span className="label-actions">
                  {form.categoryIds.length > 0 && (
                    <button
                      type="button"
                      className="label-clear-btn"
                      onClick={() => updateForm("categoryIds", [])}
                    >
                      Очистити все
                    </button>
                  )}
                  <span className="category-mode-toggle" role="radiogroup">
                    {CATEGORY_LISTS.map((item) => (
                      <button
                        key={item.value}
                        type="button"
                        role="radio"
                        aria-checked={form.categoryList === item.value}
                        className={`mode-btn ${form.categoryList === item.value ? "active" : ""}`}
                        onClick={() => updateForm("categoryList", item.value)}
                      >
                        {item.label}
                      </button>
                    ))}
                  </span>
                </span>
              </label>
              <Dropdown
                className="category-dropdown"
                mode="autocomplete"
                multiple
                options={categoryOptions
                  .filter((c) => !showSelectedOnly || form.categoryIds.includes(c.id))
                  .map((c) => ({ value: c.id, label: c.name }))}
                selected={form.categoryIds}
                onChange={(val) => updateForm("categoryIds", val)}
                placeholder="Виберіть або впишіть категорії"
              />
              <div className="subcategory-checkbox">
                <input
                  type="checkbox"
                  id="includeSubcategories"
                  checked={includeSubcategories}
                  onChange={(e) => setIncludeSubcategories(e.target.checked)}
                />
                <label htmlFor="includeSubcategories">Включати підкатегорії</label>
                <input
                  type="checkbox"
                  id="showSelectedOnly"
                  className="show-selected-checkbox"
                  checked={showSelectedOnly}
                  onChange={(e) => setShowSelectedOnly(e.target.checked)}
                />
                <label htmlFor="showSelectedOnly">Показувати тільки вибрані</label>
              </div>
              {/* Суворість — збережене правило бота, на відміну від двох чекбоксів вище */}
              <div className="subcategory-checkbox">
                <input
                  type="checkbox"
                  id="categoryStrict"
                  checked={form.categoryStrict}
                  onChange={(e) => updateForm("categoryStrict", e.target.checked)}
                />
                <label htmlFor="categoryStrict">Суворо, тільки за списком</label>
              </div>
              <p className="category-rule-hint">
                {categoryRuleHint(form.categoryList, form.categoryStrict)}
              </p>
            </div>
          </>
        )}
      </div>

      <div className="add-modal-footer">
        <button className="btn-cancel" onClick={onClose}>
          Скасувати
        </button>
        {activeTab === "settings" && (
          <button className="btn-save" onClick={handleSave} disabled={!isDirty}>
            Зберегти
          </button>
        )}
      </div>
    </AddModal>
  );
};

export default ManagerSettingsModal;
